import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Validators,
  FormGroup,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  DsButtonV2Component,
  DsCardComponent,
  DsInputTextComponent,
} from '@customdesignsystem';
import {
  customRutFormValidator,
  removeDotsFromText,
} from 'src/app/apv/core/utils/functions';
import { MAIL_REGEX } from 'src/app/shared/common/constants';
import { StepperLateralComponent } from '../../components/stepper-lateral/stepper-lateral.component';
import {
  EMPLOYER_FIELDS,
  STEP_THREE,
} from '../../constants/add-employer.constant';
import { SUBSCRIPTION_URLS } from '../../constants/subscription-routes.constant';
import {
  isValidMail,
  toAddEmployerData,
  toEmployerSelectionFromLookup,
} from '../../mappers/employer.mapper';
import { IAddEmployerData } from '../../models/subscription.model';
import { SubscriptionApiService } from '../../services/subscription-api.service';
import { SubscriptionStore } from '../../services/subscription.store';

const CURRENT_STEP_INDEX = 2;

@Component({
  selector: 'app-add-employer-page',
  templateUrl: './add-employer.page.html',
  styleUrls: ['./add-employer.page.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    StepperLateralComponent,
    DsButtonV2Component,
    DsCardComponent,
    DsInputTextComponent,
  ],
})
export class AddEmployerPage implements OnInit {
  readonly stepThree = STEP_THREE;
  readonly employerFields = EMPLOYER_FIELDS;
  readonly currentStepIndex = CURRENT_STEP_INDEX;

  formRut = new FormGroup({
    rut: new FormControl('', [Validators.required, customRutFormValidator]),
  });

  formEmail = new FormGroup({
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(MAIL_REGEX),
    ]),
  });

  employerData: IAddEmployerData | null = null;
  rutNotFound: boolean = false;
  isSearchingEmployer: boolean = false;
  emailSaved: boolean = false;
  emailTouched: boolean = false;
  isEditStepper: boolean = false;

  constructor(
    private readonly api: SubscriptionApiService,
    private readonly store: SubscriptionStore,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.isEditStepper = this.store.editMode();
    this.restoreSavedEmployer();
  }

  private restoreSavedEmployer(): void {
    const lookup = this.store.employer()?.lookup;

    if (!lookup) return;

    this.employerData = lookup;
    this.formRut
      .get('rut')
      ?.setValue(removeDotsFromText(lookup.employerFullRut).replace('-', ''));
    this.formEmail.get('email')?.setValue(lookup.employerEmail);
    this.emailSaved = true;
  }

  get canSearch(): boolean {
    return this.formRut.valid && !this.isSearchingEmployer;
  }

  get isEmailInvalid(): boolean {
    return this.emailTouched && this.formEmail.get('email')!.invalid;
  }

  get canToggleEmail(): boolean {
    return this.emailSaved || this.formEmail.valid;
  }

  get canContinue(): boolean {
    return !!this.employerData && this.emailSaved;
  }

  employerValue(key: keyof IAddEmployerData): string {
    return this.employerData ? this.employerData[key] : '';
  }

  async searchEmployer(): Promise<void> {
    if (!this.canSearch) return;

    this.employerData = null;
    this.rutNotFound = false;
    this.emailSaved = false;
    this.emailTouched = false;
    this.formEmail.reset();
    this.isSearchingEmployer = true;

    try {
      const rut = removeDotsFromText(
        this.formRut.get('rut')?.value ?? ''
      ).replace('-', '');

      const result = await this.api.findEmployer(rut);

      if (!result) {
        this.rutNotFound = true;
        return;
      }

      this.employerData = toAddEmployerData(result);
      this.formEmail.get('email')?.setValue(this.employerData.employerEmail);
      this.emailSaved = isValidMail(this.employerData.employerEmail);
    } catch (error) {
      console.error('Error searching employer', error);
      this.rutNotFound = true;
    } finally {
      this.isSearchingEmployer = false;
    }
  }

  get rutValue(): string {
    return String(this.formRut.get('rut')?.value ?? '');
  }

  get emailValue(): string {
    return String(this.formEmail.get('email')?.value ?? '');
  }

  onRutChange(value: string): void {
    this.formRut.get('rut')?.setValue(value);
    this.rutNotFound = false;
  }

  onEmailChange(value: string): void {
    this.formEmail.get('email')?.setValue(value);
    this.emailTouched = false;
  }

  onEmailBlur(): void {
    this.emailTouched = true;
  }

  toggleEmail(): void {
    if (this.emailSaved) {
      this.emailSaved = false;
      return;
    }

    if (this.formEmail.invalid || !this.employerData) {
      this.emailTouched = true;
      return;
    }

    this.emailSaved = true;
    this.emailTouched = false;
  }

  goToNextStep(): void {
    if (!this.canContinue) return;

    this.saveEmployer();
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  goBack(): void {
    if (this.isEditStepper) {
      this.backToConfirmation();
      return;
    }

    this.goToPreviousStep();
  }

  saveChanges(): void {
    if (!this.canContinue) return;

    this.saveEmployer();
    this.backToConfirmation();
  }

  private saveEmployer(): void {
    if (!this.employerData) return;

    this.store.setEmployer(
      toEmployerSelectionFromLookup({
        ...this.employerData,
        employerEmail: String(this.formEmail.get('email')?.value ?? ''),
      })
    );
  }

  private backToConfirmation(): void {
    this.store.setEditMode(false);
    this.router.navigate([SUBSCRIPTION_URLS.confirm]);
  }

  goToPreviousStep(): void {
    this.router.navigate([SUBSCRIPTION_URLS.regimeFund]);
  }
}
